import {
  DataGrid,
  GridColDef,
  GridPagination,
  GridSortDirection,
  GridValidRowModel,
  useGridApiContext,
  gridPageCountSelector,
  useGridSelector,
} from "@mui/x-data-grid";
import executeRequest from "Infrastructure/WebClient";
import { ErrorResponse } from "Infrastructure/WebClient/Client";
import { useEffect, useRef, useState } from "react";
import LoadingPage from "./LoadingPage";
import NotFoundPage from "pages/private/NotFoundPage";
import { Box, TablePaginationProps } from "@mui/material";
import MuiPagination from "@mui/material/Pagination";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import SearchFilter, { FilterConfig } from "./SearchFilter";

type SearchResponse = {
  items: GridColDef<GridValidRowModel>[];
  totalItems: number;
  pageNumber: number;
  totalPages: number;
};

type SearchResult<T> = SearchResponse | ErrorResponse | null;

type SearchTableProps<T> = {
  columns: GridColDef[];
  additionalFilters?: Record<string, string>;
  filterConfig?: FilterConfig;
  endpoint: string;
};

type SearchRequest = {
  pageNumber: number;
  pageSize: number;
  orderField: string;
  orderDirection: string;
  filters: Record<string, string>;
};

export default function SearchTable<T>({
  columns,
  additionalFilters = {},
  endpoint,
  filterConfig,
}: SearchTableProps<T>) {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  for (var i = 0; i < columns.length; i++) {
    columns[i].headerName = t(`FORM.FIELD.${columns[i].field}`);
  }

  const [searchResult, setSearchResult] = useState<SearchResult<T>>(null);
  const [loading, setLoading] = useState(false);
  const ranOnce = useRef<boolean>(false);
  const [searchRequest, setSearchRequest] = useState<SearchRequest>(
    getDefaultSearchRequest(searchParams)
  );

  useEffect(() => {
    if (!ranOnce.current) {
      ranOnce.current = true;
      setLoading(true);
      let filtersToApply: Record<string, any> = {};
      if (filterConfig) {
        Object.keys(searchRequest.filters).forEach((field: string) => {
          const appliedFilter = filterConfig[field];
          let value = searchRequest.filters[field];
          if (appliedFilter) {
            if (appliedFilter === "string") {
              value = `*${value}*`;
            }
          }
          filtersToApply[field] = value;
        });
      } else {
        filtersToApply = searchRequest.filters;
      }

      const searchQuery = {
        pageNumber: searchRequest.pageNumber + 1,
        pageSize: searchRequest.pageSize,
        orderBy: `${
          searchRequest.orderField
        },${searchRequest.orderDirection?.toUpperCase()}`,
        ...filtersToApply,
      };

      setSearchParams({
        pageNumber: `${searchRequest.pageNumber + 1}`,
        pageSize: `${searchRequest.pageSize}`,
        orderBy: `${
          searchRequest.orderField
        },${searchRequest.orderDirection?.toUpperCase()}`,
        ...searchRequest.filters,
      });

      executeRequest<SearchResult<T>>(endpoint, {
        ...additionalFilters,
        ...searchQuery,
      }).then((result: SearchResult<T>) => {
        setSearchResult(result);
        setLoading(false);
      });
    }
  }, [searchRequest]);

  function onPageChange(e: { pageSize: number; page: number }) {
    updateSearchRequest({
      pageNumber: e.page,
      pageSize: e.pageSize,
    });
  }

  function onSortChange(e: any) {
    let orderField;
    let orderDirection;
    if (e.length > 0) {
      orderField = e[0].field;
      orderDirection = e[0].sort;
    } else {
      orderField = "createdAt";
      orderDirection = "desc";
    }
    updateSearchRequest({
      orderField: orderField,
      orderDirection: orderDirection,
      pageNumber: 0,
    });
  }

  function updateSearchRequest(fields: any) {
    const newSearchRequest = {
      ...searchRequest,
      ...fields,
    };
    if (!deepEqual(searchRequest, newSearchRequest)) {
      ranOnce.current = false;
      setSearchRequest(newSearchRequest);
    }
  }

  function updateFilters(filters: any) {
    updateSearchRequest({ filters: filters });
  }

  if (searchResult && "items" in searchResult) {
    return (
      <>
        {filterConfig && (
          <Box paddingBottom={2}>
            <SearchFilter
              filterConfig={filterConfig}
              updateFilters={updateFilters}
              filterValues={searchRequest.filters}
            />
          </Box>
        )}

        <Box
          style={{
            width: "100%",
            border: "1px solid lightgray",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <DataGrid
            rows={searchResult.items}
            columns={columns}
            getRowId={(row) => row.publicId}
            rowCount={searchResult.totalItems}
            initialState={{
              sorting: {
                sortModel: [
                  {
                    field: searchRequest.orderField,
                    sort: searchRequest.orderDirection as GridSortDirection,
                  },
                ],
              },
              pagination: {
                paginationModel: {
                  page: searchRequest.pageNumber,
                  pageSize: searchRequest.pageSize,
                },
              },
            }}
            //   slots={{ toolbar: GridToolbar }}
            sortingOrder={["desc", "asc"]}
            paginationMode="server"
            pagination={true}
            onPaginationModelChange={onPageChange}
            pageSizeOptions={[10, 20, 50, 100]}
            loading={loading}
            sortingMode="server"
            sortModel={[
              {
                field: searchRequest.orderField,
                sort: searchRequest.orderDirection as GridSortDirection,
              },
            ]}
            onSortModelChange={onSortChange}
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                  fontSize: "large",
                },
              },
            }}
            slots={{
              pagination: CustomPagination,
            }}
          />
        </Box>
      </>
    );
  }

  if (searchResult && "error" in searchResult) {
    return <NotFoundPage />;
  }

  return <LoadingPage />;
}

function getDefaultSearchRequest(searchParams: any) {
  const paramsObject: any = {};
  for (const [key, value] of searchParams.entries()) {
    if (paramsObject[key]) {
      if (!Array.isArray(paramsObject[key])) {
        paramsObject[key] = [paramsObject[key]];
      }
      paramsObject[key].push(value);
    } else {
      paramsObject[key] = value;
    }
  }
  const { pageNumber, pageSize, orderBy, ...others } = paramsObject;

  var orderField, orderDirection;
  if (orderBy && (orderBy.match(new RegExp(",", "g")) || []).length === 1) {
    const orderParts = orderBy.split(",");
    orderField = orderParts[0];
    orderDirection = orderParts[1].toLowerCase();
  } else {
    orderField = "createdAt";
    orderDirection = "desc";
  }

  return {
    pageNumber: pageNumber ? parseInt(pageNumber) - 1 : 0,
    pageSize: pageSize ? parseInt(pageSize) : 10,
    orderField: orderField,
    orderDirection: orderDirection,
    filters: others,
  } as SearchRequest;
}

function Pagination({
  page,
  onPageChange,
  className,
}: Pick<TablePaginationProps, "page" | "onPageChange" | "className">) {
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <MuiPagination
      color="primary"
      className={className}
      count={pageCount}
      page={page + 1}
      onChange={(event, newPage) => {
        onPageChange(event as any, newPage - 1);
      }}
    />
  );
}

function CustomPagination(props: any) {
  return <GridPagination ActionsComponent={Pagination} {...props} />;
}

function deepEqual(obj1: any, obj2: any) {
  if (obj1 === obj2) return true;
  if (
    typeof obj1 !== "object" ||
    obj1 === null ||
    typeof obj2 !== "object" ||
    obj2 === null
  ) {
    return false;
  }
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }
  for (let key of keys1) {
    if (!keys2.includes(key)) {
      return false;
    }
    if (!deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }
  return true;
}
